import { parse, stringify } from 'yaml';

/** The longest trigger, in characters — the agentskills.io limit on a skill name. Client twin of `TRIGGER_MAX` in `skills.service.ts`. */
export const TRIGGER_MAX = 64;

/**
 * The agentskills.io name shape: 1-64 lowercase letters, digits and hyphens, no leading or trailing
 * hyphen, no `--`. Client twin of `TRIGGER_RE` in
 * `cti-conversation-service/src/conversations/skills.service.ts` — the dialog validates against the
 * exact same shape the server enforces on create and change, so a client-side pass never lets
 * through a trigger the server would 400 on.
 */
export const TRIGGER_RE = /^[a-z0-9](?:[a-z0-9]|-(?=[a-z0-9])){0,63}$/;

/** What a trigger already stored may look like: the old shape, which allowed a trailing hyphen and `--`. Client twin of `EXISTING_TRIGGER_RE` in `skills.service.ts`. */
const EXISTING_TRIGGER_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

/** Shown under a trigger field whenever the value doesn't yet match `TRIGGER_RE`'s shape. */
export const TRIGGER_FORMAT_HINT =
  'Use 1 to 64 lowercase letters, numbers and hyphens. Do not start or end with a hyphen, and do not use two hyphens in a row.';

/**
 * True when `trigger` may be saved. A new or changed trigger must match `TRIGGER_RE`; the trigger
 * a skill already has (`existingTrigger`) passes in its old shape too, so a skill saved under the
 * old rules stays editable until its trigger is deliberately changed. Client twin of the server's
 * `assertChangedTriggerValid`.
 */
export function isTriggerValid(
  trigger: string,
  existingTrigger?: string,
): boolean {
  if (TRIGGER_RE.test(trigger)) return true;
  return trigger === existingTrigger && EXISTING_TRIGGER_RE.test(trigger);
}

/**
 * Live trigger-field error message, shared by `SkillEditorDialog`'s write form and `SkillDetail`'s
 * inline edit form so both surfaces validate and report a bad/conflicting trigger the same way. A
 * 409 (`triggerConflict`) takes priority over a shape mismatch — the value could be syntactically
 * fine and still be taken. Returns `null` once the trigger is either empty (not-yet-typed, not an
 * error yet) or valid and free.
 */
export function triggerErrorMessage(
  trigger: string,
  triggerConflict: boolean,
  existingTrigger?: string,
): string | null {
  if (triggerConflict) return 'This trigger is already taken — pick another.';
  if (trigger.length > 0 && !isTriggerValid(trigger, existingTrigger))
    return TRIGGER_FORMAT_HINT;
  return null;
}

/** Lowercase-kebab tag (Jira-labels style), up to 24 chars. Client twin of `TAG_RE` in `skills.service.ts`. */
export const TAG_RE = /^[a-z0-9][a-z0-9-]{0,23}$/;

/** Upper bound on how many tags one skill may carry. Client twin of `TAGS_MAX` in `skills.service.ts`. */
export const TAGS_MAX = 8;

/**
 * Turns free typing into a valid tag the same way `deriveTriggerFromTitle` shapes a trigger:
 * lowercased, non-alphanumeric runs collapsed to a single hyphen, trimmed, capped to the 24-char
 * tag limit. Returns an empty string when nothing alphanumeric survives, so the caller just
 * ignores the entry instead of surfacing an error state.
 */
export function sanitizeTag(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24);
}

/** Upper bound on `instructions` text. Client twin of `INSTRUCTIONS_MAX` in `skills.service.ts`. */
export const INSTRUCTIONS_MAX = 64_000;

/** Instructions length past which a skill is "heavy" and delivered via `load_skill` instead of being inlined. Client twin of `INLINE_MAX` in `skills.service.ts` — used only for the editor's hint copy, not for any client-side rejection. */
export const INLINE_MAX = 8_000;

/**
 * The optional frontmatter keys of the skill specification that a skill keeps through import and
 * export. Client twin of `PRESERVED_FRONTMATTER_KEYS` in
 * `cti-conversation-service/src/conversations/skill-import.ts`.
 */
const PRESERVED_FRONTMATTER_KEYS = [
  'license',
  'compatibility',
  'metadata',
  'allowed-tools',
];

/** Upper bound on the kept frontmatter. Client twin of `FRONTMATTER_EXTRA_MAX` in `skills.service.ts`. */
export const FRONTMATTER_EXTRA_MAX = 4_000;

/** Shown when an imported file's kept frontmatter is over the limit and the skill imports without it. */
export const FRONTMATTER_EXTRA_TOO_LONG_NOTE =
  'The license and metadata lines are over 4,000 characters, so the skill imports without them.';

/** The kept frontmatter to send, or null when there is none or it is over the server's limit. */
export function sendableFrontmatterExtra(extra: string | null): string | null {
  return extra !== null && extra.length <= FRONTMATTER_EXTRA_MAX ? extra : null;
}

/** Raised by {@link parseFrontmatter} when the frontmatter cannot be read; its message names the problem and is safe to show. */
export class FrontmatterError extends Error {}

/** How YAML text is read: every scalar stays a string, so `yes`, `1.0` and `null` keep the text the author wrote. */
const FRONTMATTER_READ_OPTIONS = { schema: 'failsafe' } as const;

/** How kept keys are written back: one line per value. */
const FRONTMATTER_WRITE_OPTIONS = { lineWidth: 0 } as const;

/** Reads `text` as one YAML map; throws a {@link FrontmatterError} when it is invalid YAML or not a map. */
function readFrontmatterMap(text: string): Record<string, unknown> {
  let value: unknown;
  try {
    value = parse(text, FRONTMATTER_READ_OPTIONS);
  } catch (error) {
    const reason = (error as Error).message.split('\n')[0];
    throw new FrontmatterError(
      `SKILL.md frontmatter is not valid YAML: ${reason}`,
    );
  }
  if (value === null || value === undefined) return {};
  if (typeof value !== 'object' || Array.isArray(value)) {
    throw new FrontmatterError(
      'SKILL.md frontmatter must be a YAML map of keys',
    );
  }
  return value as Record<string, unknown>;
}

/** Re-serializes the preserved keys of `map`, in source order, as YAML text; null when none is present. */
function serializePreservedFrontmatter(
  map: Record<string, unknown>,
): string | null {
  const kept = Object.fromEntries(
    Object.entries(map).filter(([key]) =>
      PRESERVED_FRONTMATTER_KEYS.includes(key),
    ),
  );
  return Object.keys(kept).length > 0
    ? stringify(kept, FRONTMATTER_WRITE_OPTIONS).trimEnd()
    : null;
}

/** A string frontmatter field with the block scalar's trailing newlines dropped; undefined when absent or empty. Throws for a map or list. */
function frontmatterString(
  map: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = map[key];
  if (value === undefined) return undefined;
  if (typeof value !== 'string') {
    throw new FrontmatterError(`frontmatter ${key} must be a string`);
  }
  return value.replace(/\n+$/, '').trim() || undefined;
}

/**
 * Splits a Claude-skill-shaped markdown file into its `---`-delimited frontmatter (`name`,
 * `description`, and the preserved optional keys as `extra`) and body. Client twin of the server's
 * `parseFrontmatter` (`cti-conversation-service/src/conversations/skill-import.ts`) — same regex,
 * same YAML library and options, same errors, copied on purpose so the two parsers can never
 * silently disagree on what "the frontmatter" means for the same input. Throws a
 * {@link FrontmatterError} when the frontmatter is not valid YAML or not a map. Returns the whole
 * text as `body` when there is no frontmatter block.
 */
export function parseFrontmatter(md: string): {
  name?: string;
  description?: string;
  extra: string | null;
  body: string;
} {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(md);
  if (!match) return { extra: null, body: md.trim() };
  const map = readFrontmatterMap(match[1] ?? '');
  return {
    name: frontmatterString(map, 'name'),
    description: frontmatterString(map, 'description'),
    extra: serializePreservedFrontmatter(map),
    body: md.slice(match[0].length).trim(),
  };
}

/**
 * Derives a valid trigger candidate from an uploaded filename, for a `.md`/`.txt` import whose
 * frontmatter has no `name` (e.g. "My Report Skill.md" -> "my-report-skill"). Client twin of the
 * server's `slugifyFilename` in `skill-import.ts`, minus the crypto-random fallback (no
 * `node:crypto` in the browser bundle) — falls back to a timestamp-based slug instead so the
 * result is still non-empty and unique enough for a same-batch collision check.
 */
export function slugifyFilename(filename: string): string {
  const withoutExtension = filename.replace(/\.[^./]+$/, '');
  const slug = withoutExtension
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, TRIGGER_MAX)
    .replace(/-+$/, '');
  return slug || `skill-${Date.now().toString(36).slice(-8)}`;
}

/**
 * Extensions a whole skill bundle arrives under. Both are plain zips: `.zip` is what a hand-packed
 * folder and our own export use, and `.skill` is what Claude's own skill export writes — the same
 * archive, one top-level folder holding `SKILL.md` plus `references/`, `assets/` and `scripts/`,
 * only wearing a different name. There is no separate `.skill` container format to parse.
 */
export const BUNDLE_EXTENSIONS = ['.zip', '.skill'] as const;

/** The `accept` attribute for the import file input: a single markdown/text export, or a whole bundle. */
export const SKILL_IMPORT_ACCEPT = `.md,.txt,${BUNDLE_EXTENSIONS.join(',')}`;

/**
 * True when `filename` names a bundle rather than a single markdown export. This is the branch that
 * decides whether a file is posted to the server's zip parser or read as text — get it wrong for a
 * bundle and `readFileText` turns the archive's bytes into a garbage skill body with no error.
 */
export function isBundleFilename(filename: string): boolean {
  const lowered = filename.toLowerCase();
  return BUNDLE_EXTENSIONS.some((extension) => lowered.endsWith(extension));
}
